/**
 * Shared cache for getTax.
 *
 * getTax(amount, province, phoneNumber) is deterministic for a given set of
 * inputs — the same cart total, in the same province, on the same number, is
 * taxed the same way — so the three arguments are the whole cache key.
 *
 * The cache lives at module level rather than in the component because
 * OrderSummary is mounted per page: every page renders its own CheckoutLayout,
 * so navigating remounts it and a component-held cache starts empty. That is why
 * moving between pages re-taxed amounts that had already been looked up.
 *
 * Holding the in-flight promise also collapses concurrent callers, which matters
 * because the first-payment and monthly lookups can be for the same amount, and
 * because the summary effect can run twice before the first response lands.
 */

const cache = new Map()

export const fetchTax = (getTax, amount, province, phoneNumber) => {
  // There is nothing to tax without an amount, or without either a province or
  // a phone number. Both are valid on their own: the 911 path passes '' for the
  // province so the service infers it from the number, and the path without 911
  // passes a province for a port-in, whose number is not in our pool. Requiring
  // the number alone skipped the lookup for port-ins, which left the summary
  // with no tax province and blocked promo codes with "Province is required".
  if (!amount || (!province && !phoneNumber)) return Promise.resolve(null)

  const key = `${amount}|${province}|${phoneNumber}`
  const cached = cache.get(key)
  if (cached) return cached

  const request = Promise.resolve(getTax(amount, province, phoneNumber))
    .then((response) => {
      // Keep only a real answer, so a failed lookup is retried rather than
      // remembered as "no tax".
      if (!response) cache.delete(key)
      return response
    })
    .catch((error) => {
      cache.delete(key)
      throw error
    })

  cache.set(key, request)
  return request
}

export const resetTaxCache = () => cache.clear()
